// src/pages/widgets/TenantDetailModal.jsx
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { formatDateTimeISO } from "../../utils/date";
import styles from "./Widget.module.css";

const PLAN_MAP = {
    1: { name: "Basic Plan (종량제)", discount: 0 },
    2: { name: "Personal Plan (정액제)", discount: 0 },
    3: { name: "Team Plan", discount: 5 },
    4: { name: "Business Plan", discount: 15 },
    5: { name: "Enterprise Plan", discount: 30 },
    6: { name: "Enterprise Plus", discount: null }, // 별도 할인율
};

export default function TenantDetailModal({ open, detail, onClose }) {
    const closeBtnRef = useRef(null);
    const [discountRate, setDiscountRate] = useState("");
    const [saving, setSaving] = useState(false);

    // 모달 열릴 때 body 스크롤 잠금 + ESC 닫기
    useEffect(() => {
        if (!open) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);

        setTimeout(() => closeBtnRef.current?.focus(), 0);

        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    const status = String(detail?.status ?? "").trim().toUpperCase();
    const statusMeta =
        {
            WAITING: { label: "대기", cls: styles.tdmBadgeWaiting },
            APPROVED: { label: "승인", cls: styles.tdmBadgeApproved },
            PENDING: { label: "보류", cls: styles.tdmBadgePending },
            REJECTED: { label: "거절", cls: styles.tdmBadgeRejected },
        }[status] || { label: status || "-", cls: styles.tdmBadge };

    const initials = (detail?.org_name || "?")
        .split(/\s+/)
        .slice(0, 2)
        .map((s) => s[0])
        .join("")
        .toUpperCase();

    const payPlan = detail?.pay_plan ?? 0;
    const planInfo = PLAN_MAP[payPlan] ?? { name: "-", discount: null };
    const isEnterprisePlus = payPlan === 6;

    // 할인율 초기화
    useEffect(() => {
        if (!detail) return;

        if (isEnterprisePlus) {
            if (detail.pay_rate != null) {
                setDiscountRate(String(detail.pay_rate));
            } else {
                setDiscountRate("");
            }
        } else {
            const base = planInfo.discount ?? 0;
            setDiscountRate(String(base));
        }
    }, [detail, isEnterprisePlus, planInfo.discount]);

    // detail 이 없으면 그냥 안 띄움 (기존 방식 유지)
    if (!open || !detail) return null;

    const handleSave = async () => {
        if (!isEnterprisePlus) {
            onClose?.();
            return;
        }

        const v = Number(discountRate);
        if (Number.isNaN(v) || v < 0 || v > 100) {
            alert("할인율은 0~100 사이의 숫자로 입력해주세요.");
            return;
        }

        try {
            setSaving(true);

            const res = await fetch(`/api/system/tenants/${detail.key}/discount`, {
                    method: "PUT",                    // 또는 "PATCH" 사용 가능 (서버와 맞추기)
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({ pay_rate: v }),  // 🔹 서버에서 받는 필드명
                }
            );

            if (!res.ok) {
                const msg = await res.text().catch(() => "");
                console.error("할인율 업데이트 실패:", res.status, msg);
                alert("할인율 저장에 실패했습니다.");
                return;
            }

            // 필요하면 여기서 detail.pay_rate 를 갱신하거나
            // react-query invalidate 호출 등을 할 수 있음
            alert("할인율이 저장되었습니다.");
            onClose?.();
        } catch (err) {
            console.error("할인율 업데이트 오류:", err);
            alert("할인율 저장 중 오류가 발생했습니다.");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div
            className={styles.tdmOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="테넌트 상세"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
        >
            <div className={styles.tdmCard} onClick={(e) => e.stopPropagation()}>
                {/* 헤더 */}
                <div className={styles.tdmHead}>
                    <div className={styles.tdmHeadLeft}>
                        <div className={styles.tdmAvatarWrap}>
                            <div className={styles.tdmAvatar} aria-hidden>
                                {initials}
                            </div>
                        </div>
                        <div className={styles.tdmTitleText}>
                            <div className={styles.tdmTitleLabel}>테넌트 정보</div>
                            <div className={styles.tdmNameRow}>
                                <span
                                    className={styles.tdmName}
                                    title={detail.org_name || ""}
                                >
                                    {detail.org_name}
                                </span>
                                <span className={styles.tdmIdChip}>
                                    ID&nbsp;·&nbsp;
                                    <span className={styles.tdmMono}>
                                        {detail.key ?? detail.id ?? "-"}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={styles.tdmHeadActions}>
                        <button
                            type="button"
                            className={styles.tdmCloseBtn}
                            onClick={onClose}
                            ref={closeBtnRef}
                            aria-label="닫기"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* 본문 */}
                <div className={styles.tdmBody}>
                    <div className={styles.tdmGrid}>
                        {/* 기존 공통 정보들 */}
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>조직명</div>
                            <div className={styles.tdmVal}>{detail.org_name}</div>
                        </div>
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>UUID</div>
                            <div className={`${styles.tdmVal} ${styles.tdmMono}`}>
                                {detail.org_key}
                            </div>
                        </div>
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>조직 유형</div>
                            <div className={styles.tdmVal}>
                                {detail.org_type ? "개인" : "조직"}
                            </div>
                        </div>
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>상태</div>
                            <div className={styles.tdmVal}>
                                <span className={statusMeta.cls}>{statusMeta.label}</span>
                            </div>
                        </div>
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>생성일</div>
                            <div className={styles.tdmVal}>
                                {formatDateTimeISO(detail.created_at, { timeStyle: "medium" })}
                            </div>
                        </div>
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>수정일</div>
                            <div className={styles.tdmVal}>
                                {detail.updated_at
                                    ? formatDateTimeISO(detail.updated_at, {
                                        timeStyle: "medium",
                                    })
                                    : "-"}
                            </div>
                        </div>
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>관리자</div>
                            <div className={`${styles.tdmVal} ${styles.tdmMono}`}>
                                {detail.name}
                            </div>
                        </div>
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>관리자 이메일</div>
                            <div className={`${styles.tdmVal} ${styles.tdmMono}`}>
                                {detail.email}
                            </div>
                        </div>

                        {/* ✅ 요금제: 다른 필드처럼 라벨 + 값 */}
                        <div className={styles.tdmItem}>
                            <div className={styles.tdmKey}>요금제</div>
                            <div className={styles.tdmVal}>{planInfo.name}</div>
                        </div>

                        {/* ✅ 할인율: 기본 요금제와 Enterprise Plus를 분리 표시 */}
                        {!isEnterprisePlus && (
                            <div className={styles.tdmItem}>
                                <div className={styles.tdmKey}>할인율</div>
                                <div className={styles.tdmVal}>
                                    {planInfo.discount ?? 0}% {/* 필요하면 "기본 할인율" 문구 추가 가능 */}
                                </div>
                            </div>
                        )}

                        {isEnterprisePlus && (
                            <div className={styles.tdmItem}>
                                <div className={styles.tdmKey}>할인율</div>
                                <div className={styles.tdmDiscountRow}>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={discountRate}
                                        onChange={(e) => setDiscountRate(e.target.value)}
                                        className={styles.tdmDiscountInput}
                                        placeholder="예: 25"
                                    />
                                    <span className={styles.tdmDiscountSuffix}>%</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 푸터 */}
                <div className={styles.tdmFoot}>
                    <button type="button" className={styles.tdmBtn} onClick={onClose}>
                        닫기
                    </button>
                    <button
                        type="button"
                        className={styles.tdmBtnPrimary}
                        onClick={handleSave}
                    >
                        {isEnterprisePlus ? "할인율 적용" : "확인"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
